/**
 * Platform bootstrap (design §2.5, §14.2; MM-D-28). The app holds no configuration naming the
 * auth service, the login UI, or the API. It asks tmm-api once, at start, on its own origin, and
 * keeps the answer IN MEMORY ONLY — no storage, no TTL, no refresh timer. A cached `loginUrl`
 * would outlive an operator changing a tenant's hostnames; staleness is bounded server-side
 * (the API caches per host for 5 minutes).
 *
 * `GET /api/bootstrap` is unauthenticated, so it is issued with `fetch` (never the configured
 * axios instance — its interceptors need a resolved auth origin, which is what this call
 * provides) and `credentials: 'omit'` explicitly.
 */
import { normalizeConsumerTerms, type ConsumerTerms } from '@/api/consumerTerms';
import { env } from './env';

export interface Bootstrap {
  tenantSlug: string;
  tenantName: string;
  tenantStatus: string;
  /** Complete URL with no query string; the SPA appends `?returnUrl=`. Used verbatim. */
  loginUrl: string;
  /** `https://` + the tenant auth host. Used verbatim — credentials and CSRF go here only. */
  authOrigin: string;
  brandOrigin: string | null;
  assetOrigin: string | null;
  forgotPasswordUrl: string | null;
  product: { slug: string | null; displayName: string | null } | null;
  deployEnv: string | null;
  /**
   * The consumer terms and the seller (TMMAPI-0156 U1, MM-D-138): the served version a subscribe
   * accepts, the two document links, and who bills. Each part is null when not served.
   */
  consumerTerms: ConsumerTerms;
}

/**
 * `status` is the HTTP status when the request reached the server, or `null` for a transport
 * failure or an unparseable body. `code` is the envelope's `error.code` when the body carried
 * one (`HOSTNAME_NOT_REGISTERED`, `TENANT_AUTH_NOT_CONFIGURED`, `UPSTREAM_UNAVAILABLE`).
 */
export class BootstrapError extends Error {
  override readonly name = 'BootstrapError';
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null = null,
  ) {
    super(message);
  }
}

const REQUIRED = ['tenantSlug', 'tenantName', 'tenantStatus', 'loginUrl', 'authOrigin'] as const;

let resolved: Bootstrap | null = null;

export async function resolveBootstrap(): Promise<Bootstrap> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/bootstrap`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new BootstrapError('Could not reach the platform bootstrap endpoint.', null);
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    if (response.ok)
      throw new BootstrapError('Bootstrap response was not valid JSON.', response.status);
  }
  if (!response.ok) {
    throw new BootstrapError(
      `Bootstrap request failed with status ${response.status}.`,
      response.status,
      envelopeCode(body),
    );
  }
  const parsed = parseBootstrap(body);
  if (!parsed) {
    throw new BootstrapError('Bootstrap response is missing a required field.', response.status);
  }
  resolved = parsed;
  return parsed;
}

/** Throws before `resolveBootstrap()` has succeeded — there is no default or composed fallback. */
export function getBootstrap(): Bootstrap {
  if (!resolved) throw new Error('getBootstrap() called before bootstrap resolved.');
  return resolved;
}

function envelopeCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const error = (body as { error?: unknown }).error;
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Every optional field degrades to null on a wrong type; origins are carried verbatim. */
function parseBootstrap(body: unknown): Bootstrap | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  for (const key of REQUIRED) {
    if (typeof b[key] !== 'string' || (b[key] as string).length === 0) return null;
  }
  const product =
    b.product && typeof b.product === 'object' ? (b.product as Record<string, unknown>) : null;
  return {
    tenantSlug: b.tenantSlug as string,
    tenantName: b.tenantName as string,
    tenantStatus: b.tenantStatus as string,
    loginUrl: b.loginUrl as string,
    authOrigin: b.authOrigin as string,
    brandOrigin: nullableString(b.brandOrigin),
    assetOrigin: nullableString(b.assetOrigin),
    forgotPasswordUrl: nullableString(b.forgotPasswordUrl),
    product: product
      ? { slug: nullableString(product.slug), displayName: nullableString(product.displayName) }
      : null,
    deployEnv: nullableString(b.deployEnv),
    consumerTerms: normalizeConsumerTerms(b),
  };
}

/** Test seam — seed or clear the resolved bootstrap without a fetch. */
export function __setBootstrapForTest(bootstrap: Bootstrap | null): void {
  resolved = bootstrap;
}
